import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateView } from './useQueries';

// Keep these in sync with the server-side clamp in routes/user-views.js.
export const MIN_COLUMN_WIDTH = 60;
export const MAX_COLUMN_WIDTH = 1200;

// Widths are committed once per drag, but a user adjusting several columns in a
// row shouldn't generate a PUT per column.
const SAVE_DEBOUNCE_MS = 600;

const storageKeyFor = (tableName) => `${tableName}-column-widths`;

/**
 * Widths are stored as { "<column_key>": <pixels> } and only contain columns the
 * user actually dragged. Everything else keeps its automatic width, so adding or
 * reordering columns later doesn't need a data migration.
 */
export const sanitizeColumnWidths = (value) => {
  let raw = value;

  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (error) {
      return {};
    }
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }

  const sanitized = {};
  Object.entries(raw).forEach(([key, width]) => {
    if (typeof key !== 'string' || key.length === 0) return;
    const numeric = Number(width);
    if (!Number.isFinite(numeric)) return;
    sanitized[key] = Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, numeric)));
  });

  return sanitized;
};

const areWidthsEqual = (a, b) => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => a[key] === b[key]);
};

// localStorage holds a { [viewKey]: widths } map per table so switching views
// swaps widths instantly, and so the built-in view (which has no database row)
// still remembers its widths.
const readWidthStore = (tableName) => {
  try {
    const saved = localStorage.getItem(storageKeyFor(tableName));
    const parsed = saved ? JSON.parse(saved) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    console.warn('Failed to load column widths:', error);
    return {};
  }
};

const writeWidthStore = (tableName, viewKey, widths) => {
  try {
    const store = readWidthStore(tableName);
    if (Object.keys(widths).length === 0) {
      delete store[viewKey];
    } else {
      store[viewKey] = widths;
    }
    localStorage.setItem(storageKeyFor(tableName), JSON.stringify(store));
  } catch (error) {
    console.warn('Failed to save column widths:', error);
  }
};

/**
 * Column widths for a list table, persisted per view.
 *
 * The saved view is the source of truth when one is selected (so widths follow
 * the user to another browser); localStorage mirrors it for instant restore and
 * is the only store for the built-in "All Items"/"All Tasks" view.
 *
 * @param {string} tableName  'items' | 'tasks' - also the localStorage namespace
 * @param {string} currentView  'default' or a saved view id
 * @param {Array} userViews  saved views from useUserViews
 * @param {boolean} persistToView  false to keep widths local only
 */
export function useColumnWidths({ tableName, currentView = 'default', userViews = [], persistToView = true }) {
  const updateViewMutation = useUpdateView();
  const viewKey = currentView == null ? 'default' : String(currentView);

  // The mutation object is a new reference every render; hold it in a ref so the
  // save callbacks below stay stable and the debounce actually debounces.
  const updateViewRef = useRef(updateViewMutation);
  useEffect(() => {
    updateViewRef.current = updateViewMutation;
  });

  const [columnWidths, setColumnWidths] = useState(() => sanitizeColumnWidths(readWidthStore(tableName)[viewKey]));

  const widthsRef = useRef(columnWidths);
  const pendingSaveRef = useRef(null); // { viewId, widths } waiting on the debounce
  const saveTimerRef = useRef(null);
  const inFlightSavesRef = useRef(0);
  const [saveEpoch, setSaveEpoch] = useState(0);

  const savedView = useMemo(() => {
    if (viewKey === 'default') return null;
    if (!Array.isArray(userViews)) return null;
    return userViews.find((view) => String(view?.id) === viewKey) || null;
  }, [userViews, viewKey]);

  const flushSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const pending = pendingSaveRef.current;
    if (!pending) return false;
    pendingSaveRef.current = null;
    inFlightSavesRef.current += 1;

    updateViewRef.current.mutate(
      { id: pending.viewId, column_widths: pending.widths },
      {
        onError: (error) => {
          // Widths still live in localStorage, so the user keeps them on this
          // machine even when the view update fails.
          console.warn('Failed to save column widths to view:', error);
        },
        onSettled: () => {
          inFlightSavesRef.current = Math.max(0, inFlightSavesRef.current - 1);
          // Re-run the load effect: it may have skipped a view switch while this
          // save was on the wire.
          setSaveEpoch((epoch) => epoch + 1);
        }
      }
    );

    return true;
  }, []);

  // Load widths for the selected view.
  useEffect(() => {
    // Commit any in-flight resize (against the view it was made on) before
    // swapping in another view's widths.
    const justSaved = flushSave();

    // While a save is on the wire the local widths are newer than anything the
    // views query can hand back, so don't let a stale refetch undo the drag.
    if (justSaved || inFlightSavesRef.current > 0) return;

    const localWidths = sanitizeColumnWidths(readWidthStore(tableName)[viewKey]);
    const viewWidths = savedView ? sanitizeColumnWidths(savedView.column_widths) : null;

    // A saved view with no stored widths yet falls back to whatever this browser
    // has, which is what a user who resized before this shipped would expect.
    // On a global view the row can't hold per-user widths, so once this browser
    // has its own they win - otherwise the next view switch would undo the drag.
    const preferLocal = savedView?.is_global === true && Object.keys(localWidths).length > 0;
    const nextWidths = !preferLocal && viewWidths && Object.keys(viewWidths).length > 0
      ? viewWidths
      : localWidths;

    if (areWidthsEqual(widthsRef.current, nextWidths)) return;

    widthsRef.current = nextWidths;
    setColumnWidths(nextWidths);
  }, [tableName, viewKey, savedView, flushSave, saveEpoch]);

  const applyWidths = useCallback((nextWidths) => {
    widthsRef.current = nextWidths;
    setColumnWidths(nextWidths);
    writeWidthStore(tableName, viewKey, nextWidths);

    // Global views are one shared row, so widths stay in this browser's store -
    // otherwise one user's drag would resize the table for everyone. Column widths
    // are a personal display preference either way.
    if (!persistToView || !savedView?.id || savedView.is_global) return;

    pendingSaveRef.current = { viewId: savedView.id, widths: nextWidths };
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      flushSave();
    }, SAVE_DEBOUNCE_MS);
  }, [tableName, viewKey, persistToView, savedView, flushSave]);

  // Pass a null/undefined width to clear a column back to its automatic size.
  const handleColumnResize = useCallback((columnKey, width) => {
    if (!columnKey) return;

    const next = { ...widthsRef.current };
    if (width == null) {
      if (!(columnKey in next)) return;
      delete next[columnKey];
    } else {
      const clamped = Math.round(Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Number(width))));
      if (!Number.isFinite(clamped) || next[columnKey] === clamped) return;
      next[columnKey] = clamped;
    }

    applyWidths(next);
  }, [applyWidths]);

  const resetColumnWidths = useCallback(() => {
    if (Object.keys(widthsRef.current).length === 0) return;
    applyWidths({});
  }, [applyWidths]);

  // Don't lose a width the user just dragged by navigating away mid-debounce.
  useEffect(() => () => flushSave(), [flushSave]);

  return {
    columnWidths,
    handleColumnResize,
    resetColumnWidths,
    hasCustomWidths: Object.keys(columnWidths).length > 0
  };
}

export default useColumnWidths;
